import React, { useEffect, useState } from 'react';
import { EFFORT_STEP, normaliseEffort } from '../utils/effort';

/**
 * A quarter-day effort field (T469).
 *
 * ⛔ ADMIN ONLY. Nothing here enforces that — the caller decides whether to
 * render it, and the server strips the value from every client read (T466).
 *
 * ⚠️ IT COMMITS ON BLUR, NOT ON EVERY KEYSTROKE. Each commit is a request, an
 * audit row and a version bump, and "4" is a prefix of "4.25" — saving as you
 * type would write a wrong value on the way to the right one.
 *
 * ⚠️ THE VALUE ARRIVES AS A STRING AND THE SERVER DEMANDS A NUMBER. A number
 * input's `.value` is a string even when it looks numeric, and `''` is what an
 * emptied field gives. So the string is converted here — `''` to null, meaning
 * CLEAR — and then put through the SAME `normaliseEffort` the write path uses,
 * so the message a person sees is the message the server would have sent.
 * The server validates again; this is for the message, never for the guarantee.
 */
export default function EffortInput({ value, onCommit, saving = false, disabled = false }) {
    const [draft, setDraft] = useState(value === null || value === undefined ? '' : String(value));
    const [error, setError] = useState('');

    // Follow the stored value when it changes underneath - another admin's edit
    // arriving over the WebSocket, or the modal being pointed at another task.
    // Skipped while the field is focused, or a refresh mid-edit would discard
    // what is being typed.
    useEffect(() => {
        setDraft(value === null || value === undefined ? '' : String(value));
        setError('');
    }, [value]);

    const commit = () => {
        const raw = draft.trim();
        // ⚠️ An emptied field is a CLEAR, not a zero and not an error.
        const parsed = raw === '' ? null : Number(raw);

        try {
            // Same validator as the server, so the wording cannot drift.
            normaliseEffort(parsed, 'Effort');
        } catch (err) {
            setError(err.message.replace(/^Effort /, ''));
            return;
        }

        setError('');
        // Nothing to do if it has not moved. Saves a request, an audit row and
        // a version bump on every blur.
        const current = value === null || value === undefined ? null : value;
        if (parsed === current) return;

        onCommit(parsed);
    };

    return (
        <div className="flex items-center gap-2">
            <input
                type="number"
                step={EFFORT_STEP}
                min="0"
                value={draft}
                disabled={disabled || saving}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={e => {
                    if (e.key === 'Enter') e.target.blur();
                    if (e.key === 'Escape') {
                        setDraft(value === null || value === undefined ? '' : String(value));
                        setError('');
                    }
                }}
                aria-label="Effort in days"
                className={`w-24 px-2 py-1 border rounded text-sm ${
                    error ? 'border-red-400' : 'border-gray-300'
                }`}
                placeholder="-"
            />
            {saving && (
                <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            )}
            {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
    );
}
